/**
 * <s-show-more> — Reveals server-rendered list items in batches.
 *
 * Commands (dispatched via commandfor/command on a descendant button):
 *   --show-more — First use unhides the next `batch` items marked [data-more-item][hidden];
 *                 the second use unhides all remaining items. Focus moves to the first
 *                 newly revealed item's link.
 *
 * Required child markup:
 *   [data-more-item]   — Items that start with the `hidden` attribute.
 *   [data-more-button] — The trigger; hidden once nothing is left to reveal.
 *
 * Element attributes:
 *   batch — Number of items to reveal per command (default 20).
 */
class SShowMore extends HTMLElement {
  #ac;
  #clicks = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener(
      'command',
      (e) => {
        if (e.command === '--show-more') this.#reveal();
      },
      { signal: this.#ac.signal },
    );
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #batch() {
    const value = parseInt(this.getAttribute('batch'), 10);
    return Number.isFinite(value) && value > 0 ? value : 20;
  }

  #hiddenItems() {
    return Array.from(this.querySelectorAll('[data-more-item][hidden]'));
  }

  #reveal() {
    // First click reveals one batch; every later click reveals everything that's left.
    this.#clicks += 1;
    const hidden = this.#hiddenItems();
    const items = this.#clicks === 1 ? hidden.slice(0, this.#batch) : hidden;
    items.forEach((item) => {
      item.hidden = false;
    });
    this.#sync();
    const firstLink = items[0]?.querySelector('a[href]');
    if (firstLink) firstLink.focus();
  }

  #sync() {
    const button = this.querySelector('[data-more-button]');
    if (button) button.hidden = this.#hiddenItems().length === 0;
  }
}

if (!customElements.get('s-show-more')) customElements.define('s-show-more', SShowMore);
